import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { TransactionDetailType } from 'constants/loanApplication';
import { getConditionalArray } from 'utils/data';

import { getDocuments } from 'store/documents/selectors';
import {
  selectClosingInfo,
  selectExtendedBorrowersCommonInfo,
  selectFundingInfo,
  selectGroupedByPriorToLoanConditionsList,
  selectGroupedFilteredTasks,
  selectIsHelocPurposePurchaseFromPropertyAndLoanSummary,
  selectIsLoanLockedFromPropertyAndLoanSummary,
  selectIsLoanPurposeAdditionalLoanFromPropertyAndLoanSummary,
  selectIsLoanPurposePurchaseFromPropertyAndLoanSummary,
  selectIsLoanPurposeRefinanceFromPropertyAndLoanSummary,
  selectIsNoPpeEngineTypeFromPricingStatus,
  selectLoanActions,
  selectLoanApplicationChecklistResultsList,
  selectLoanApplicationControlUI,
  selectLoanContactsAllowedActions,
  selectLoanContactsView,
  selectLoanPurposeFromPropertyAndLoanSummary,
  selectLoanTypeFromPropertyAndLoanSummary,
  selectNormalizedLoanDataForSearch,
  selectSortedBorrowerAssets,
  selectSortedBorrowerIncomes,
  selectSortedBorrowerLiabilities,
  selectSortedBorrowerProperties,
  selectSubjectPropertyForSearch,
} from 'store/loanApplications/selectors';
import { getDocumentCategories } from 'store/system/selectors/documentCategories.selectors';
import { getDocumentTypes } from 'store/system/selectors/documentTypes.selectors';

import { ALL_BORROWERS_PATH } from '../../tabs/financialInfo/FinancialInfo.constants';
import { buildFieldConfigsAndVisibility, buildFieldsIndex } from '../builders/fields.utils';
import {
  buildNavigationIndex,
  buildQuickActionsIndex,
  buildShortcutMap,
} from '../builders/navigation.utils';
import { buildRecordsIndex } from '../builders/records.utils';
import {
  buildBorrowerSectionsIndex,
  buildChecklistsIndex,
  buildConditionsIndex,
  buildContactsIndex,
  buildDocumentCategoriesIndex,
  buildDocumentsIndex,
  buildLoanClosingSectionsIndex,
  buildTasksIndex,
} from '../builders/sections.utils';
import { buildActionsIndex } from '../UniversalSearch.search.utils';

const useSearchIndex = () => {
  const isPurchase = useSelector(selectIsLoanPurposePurchaseFromPropertyAndLoanSummary);
  const isRefinance = useSelector(selectIsLoanPurposeRefinanceFromPropertyAndLoanSummary);
  const loanPurpose = useSelector(selectLoanPurposeFromPropertyAndLoanSummary);
  const mortgageType = useSelector(selectLoanTypeFromPropertyAndLoanSummary);
  const subjectProperty = useSelector(selectSubjectPropertyForSearch);
  const loanApplicationData = useSelector(selectNormalizedLoanDataForSearch);
  const isLoanNoPpeEngineType = useSelector(selectIsNoPpeEngineTypeFromPricingStatus);
  const isLoanLocked = useSelector(selectIsLoanLockedFromPropertyAndLoanSummary);
  const { availableTabs } = useSelector(selectLoanApplicationControlUI);
  const isAdditionalLoan = useSelector(selectIsLoanPurposeAdditionalLoanFromPropertyAndLoanSummary);
  const isHelocPurposePurchase = useSelector(
    selectIsHelocPurposePurchaseFromPropertyAndLoanSummary,
  );
  const { isViewAllowed: isContactsViewAllowed, isManageAllowed: isAddContactAllowed } =
    useSelector(selectLoanContactsAllowedActions);
  const loanActions = useSelector(selectLoanActions);
  const borrowers = useSelector(selectExtendedBorrowersCommonInfo);
  const liabilities = useSelector(selectSortedBorrowerLiabilities(ALL_BORROWERS_PATH));
  const incomes = useSelector(selectSortedBorrowerIncomes(ALL_BORROWERS_PATH));
  const assets = useSelector(selectSortedBorrowerAssets(ALL_BORROWERS_PATH));
  const properties = useSelector(selectSortedBorrowerProperties(ALL_BORROWERS_PATH));
  const documents = useSelector(getDocuments);
  const documentTypes = useSelector(getDocumentTypes);
  const documentCategories = useSelector(getDocumentCategories);
  const conditionGroups = useSelector(selectGroupedByPriorToLoanConditionsList);
  const taskGroups = useSelector(selectGroupedFilteredTasks);
  const contacts = useSelector(selectLoanContactsView);
  const closingInfo = useSelector(selectClosingInfo);
  const fundingInfo = useSelector(selectFundingInfo);
  const checklistResults = useSelector(selectLoanApplicationChecklistResultsList);

  const propertyState = subjectProperty?.propertyAddress?.state;
  const isConstructionToPermanent =
    subjectProperty?.transactionDetail ===
    TransactionDetailType.CONSTRUCTION_CONVERSATION_OR_TO_PERMANENT;

  const { searchIndex, shortcutEntries } = useMemo(() => {
    const { tabNameToShortcut, shortcutMap } = buildShortcutMap({ availableTabs });

    const navigationItems = buildNavigationIndex({
      availableTabs,
      borrowers,
      liabilities,
      incomes,
      assets,
      properties,
    }).map(item => {
      const shortcutKey = tabNameToShortcut[item.label];

      return shortcutKey ? { ...item, shortcutKey } : item;
    });

    const canAddAdditionalMortgage = isPurchase || (isAdditionalLoan && isHelocPurposePurchase);

    const quickActionItems = buildQuickActionsIndex({
      availableTabs,
      canAddAdditionalMortgage,
      canAddContact: isAddContactAllowed,
    });

    const { visibleFieldIds, lockInfoConfigFields } = buildFieldConfigsAndVisibility({
      isPurchase,
      isRefinance,
      isConstructionToPermanent,
      loanPurpose,
      mortgageType,
      isLoanNoPpeEngineType,
      isLoanLocked,
      propertyState,
    });

    const fieldItems = buildFieldsIndex({
      visibleFieldIds,
      lockInfoConfigFields,
      subjectProperty,
      loanApplicationData,
    });

    const actionItems = buildActionsIndex({ loanActions });

    const recordItems = buildRecordsIndex({
      borrowers,
      liabilities,
      incomes,
      assets,
      properties,
    });

    const borrowerSectionItems = buildBorrowerSectionsIndex({ borrowers });

    const loanClosingData = {
      ...closingInfo,
      ...closingInfo?.prepaidInterestCreditData,
      ...fundingInfo,
    };

    const loanClosingItems = buildLoanClosingSectionsIndex({ loanClosingData });

    const documentItems = buildDocumentsIndex({ documents, documentTypes, documentCategories });

    const documentCategoryItems = buildDocumentCategoriesIndex({
      documentCategories,
      documents,
    });

    const conditionItems = buildConditionsIndex({ conditionGroups });

    const taskItems = buildTasksIndex({ taskGroups });

    const contactItems = getConditionalArray(isContactsViewAllowed, () =>
      buildContactsIndex({ contacts }),
    );

    const checklistItems = buildChecklistsIndex({ checklistResults });

    const shortcuts = Object.entries(shortcutMap).map(([key, { tabName }]) => ({
      key,
      tabName,
    }));

    return {
      searchIndex: [
        ...navigationItems,
        ...quickActionItems,
        ...fieldItems,
        ...actionItems,
        ...recordItems,
        ...borrowerSectionItems,
        ...loanClosingItems,
        ...documentItems,
        ...documentCategoryItems,
        ...conditionItems,
        ...taskItems,
        ...contactItems,
        ...checklistItems,
      ],
      shortcutEntries: shortcuts,
    };
  }, [
    isContactsViewAllowed,
    isPurchase,
    isRefinance,
    loanPurpose,
    mortgageType,
    isConstructionToPermanent,
    propertyState,
    isLoanNoPpeEngineType,
    isLoanLocked,
    availableTabs,
    isAdditionalLoan,
    isHelocPurposePurchase,
    isAddContactAllowed,
    loanActions,
    borrowers,
    liabilities,
    incomes,
    assets,
    properties,
    documents,
    documentTypes,
    documentCategories,
    subjectProperty,
    loanApplicationData,
    conditionGroups,
    taskGroups,
    contacts,
    closingInfo,
    fundingInfo,
    checklistResults,
  ]);

  return { searchIndex, shortcutEntries };
};

export default useSearchIndex;
